import i18n from '@/utils/i18n';
import React, { useCallback, useEffect } from 'react';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { NavigateToMicroproductPayload } from '@domain/microproducts/components/micro-product-webview-types';
import { PageContainer } from '@/components/page-container';
import { StackScreenProps } from '@react-navigation/stack';
import { StorefrontMicroProductView } from '@/screens/storefront-microproducts/storefront-micro-product-view';
import { useStorefrontMicroProducts } from '@/screens/storefront-microproducts/blocs/use-storefront-microproducts';

export type StorefrontMicroProductScreenProps = StackScreenProps<
  AppContainerStackParams,
  typeof APP_CONTAINER_ROUTES.StorefrontMicroProduct
>;

/**
 * Pushed-screen surface for a storefront microproduct.
 *
 * Since D9 the primary surface on large/tablet layouts is the in-container
 * tab (StorefrontMicroProductTab, registered in app-tabs) — this stack
 * screen remains for the small layout, which has no tab entry (the
 * Links/ExternalContentScreen split). The microproduct body itself lives in
 * the shared StorefrontMicroProductView.
 */
export const StorefrontMicroProductScreen: React.FC<
  StorefrontMicroProductScreenProps
> = ({ navigation, route }) => {
  const productId = route.params?.productId;
  const { loading, products } = useStorefrontMicroProducts();
  const { layoutSize } = useLayoutContext();
  const product = products.find((p) => p.id === productId) ?? null;
  const title =
    product?.displayName ?? i18n.t('storefront_microproducts.not_found.title');

  // The container stack hides headers and the drawer-header back affordance
  // only exists on the LARGE layout — every other layout wraps the content
  // in a back-button header so the screen is never a dead end (the
  // ExternalContentScreen pattern).
  const withEscape = (content: React.ReactElement) =>
    layoutSize === LayoutSize.LARGE ? (
      content
    ) : (
      <PageContainer
        animateNavigationHeader={false}
        navigationHeaderProps={{
          centerTitle: true,
          showBackButton: true,
          title,
        }}
      >
        {content}
      </PageContainer>
    );

  const handleNavigateToMicroproduct = useCallback(
    (payload: NavigateToMicroproductPayload) => {
      navigation.navigate(APP_CONTAINER_ROUTES.StorefrontMicroProduct, {
        productId: payload.productId,
      });
    },
    [navigation],
  );

  useEffect(() => {
    if (loading) {
      return;
    }
    navigation.setOptions({ title });
  }, [loading, navigation, title]);

  return withEscape(
    <StorefrontMicroProductView
      onNavigateToMicroproduct={handleNavigateToMicroproduct}
      productId={productId}
    />,
  );
};
